import { businessService } from '@/pages/Business/services/business.service';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import type {
  ExportJob,
  InitiateExportResponse,
} from '@/pages/Business/pages/Monitoring/models/Export';
import { TransitControlFilter, TransitControlItem } from '../models';

export type GetTransitControlListQueryArgs = {
  filters: TransitControlFilter;
  paginated: PaginatedRequest;
};

const buildTransitControlParams = (
  filters: TransitControlFilter
): Record<string, string> => ({
  ...(filters.originStatusId && { originStatusId: filters.originStatusId }),
  ...(filters.destinationStatusId && {
    destinationStatusId: filters.destinationStatusId,
  }),
  ...(filters.dateFrom && { dateFrom: filters.dateFrom.toISOString() }),
  ...(filters.dateTo && { dateTo: filters.dateTo.toISOString() }),
  ...(filters.commerceId && { commerceId: filters.commerceId }),
  ...(filters.logisticOperatorId && {
    logisticOperatorId: filters.logisticOperatorId,
  }),
  ...(filters.serviceWithOperatorIds?.length && {
    serviceWithOperatorIds: filters.serviceWithOperatorIds.join(','),
  }),
});

const transitControlApi = businessService.injectEndpoints({
  endpoints: (builder) => ({
    getTransitControlList: builder.query<
      PaginatedResponse<TransitControlItem>,
      GetTransitControlListQueryArgs
    >({
      query: ({ paginated, filters }) => ({
        url: '/transit-control',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...buildTransitControlParams(filters),
        },
      }),
      providesTags: ['TransitControl'],
    }),

    countTransitControlExport: builder.mutation<
      { count: number; limit: number },
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/transit-control/export-excel/count',
        method: 'POST',
        body,
      }),
    }),
    initiateTransitControlExport: builder.mutation<
      InitiateExportResponse,
      Record<string, string | number | string[]>
    >({
      query: (body) => ({
        url: '/transit-control/export-excel',
        method: 'POST',
        body,
      }),
    }),
    getTransitControlExportJobStatus: builder.query<ExportJob, string>({
      query: (jobId) => ({
        url: `/transit-control/export-excel/jobs/${jobId}`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTransitControlListQuery,
  useCountTransitControlExportMutation,
  useInitiateTransitControlExportMutation,
  useLazyGetTransitControlExportJobStatusQuery,
} = transitControlApi;
